import React, {useCallback, useMemo} from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import MuiTooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Filler,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip,
} from 'chart.js';
import {Chart} from 'react-chartjs-2';
import {MinimalCutSet} from '../../../../utils/minimal-cut-sets';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler, Title, Tooltip, Legend);

interface CumulativeCoverageChartProps {
    cutSets: MinimalCutSet[];
    totalProbability: number;
    chartRef?: React.RefObject<ChartJS | null>;
}


const CumulativeCoverageChart: React.FC<CumulativeCoverageChartProps> = ({ cutSets, totalProbability, chartRef }) => {
    const coverageData = useMemo(() => {
        const probByOrder = new Map<number, number>();
        const countByOrder = new Map<number, number>();
        for (const cs of cutSets) {
            probByOrder.set(cs.order, (probByOrder.get(cs.order) || 0) + cs.probability);
            countByOrder.set(cs.order, (countByOrder.get(cs.order) || 0) + 1);
        }
        const orders = Array.from(probByOrder.keys()).sort((a, b) => a - b);

        const barData = orders.map((o) => {
            const pct = totalProbability > 0 ? (probByOrder.get(o)! / totalProbability) * 100 : 0;
            return { order: o, probability: probByOrder.get(o)!, pct, count: countByOrder.get(o)! };
        });

        let cumSum = 0;
        const cumulative = barData.map((d) => {
            cumSum += d.pct;
            return cumSum;
        });

        return { barData, cumulative, orders };
    }, [cutSets, totalProbability]);

    const chartData = useMemo(() => ({
        labels: coverageData.orders.map((o) => `Order ${o}`),
        datasets: [
            {
                type: 'bar' as const,
                label: 'Contribution by order (%)',
                data: coverageData.barData.map((d) => d.pct),
                backgroundColor: 'rgba(25, 118, 210, 0.7)',
                borderColor: 'rgba(25, 118, 210, 1)',
                borderWidth: 1,
                yAxisID: 'y',
                order: 2,
            },
            {
                type: 'line' as const,
                label: 'Cumulative (%)',
                data: coverageData.cumulative,
                borderColor: 'rgba(33, 33, 33, 0.8)',
                backgroundColor: 'rgba(33, 33, 33, 0.05)',
                borderWidth: 2,
                pointRadius: 4,
                pointHoverRadius: 6,
                fill: true,
                yAxisID: 'y1',
                order: 1,
            },
        ],
    }), [coverageData]);

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index' as const,
            intersect: false,
        },
        plugins: {
            title: { display: false },
            legend: {
                position: 'bottom' as const,
                labels: { font: { size: 11 } },
            },
            tooltip: {
                callbacks: {
                    afterBody: (items: Array<{ dataIndex: number }>): string => {
                        const idx = items[0]?.dataIndex;
                        if (idx === undefined) return '';
                        const d = coverageData.barData[idx];
                        return `${d.count} cut set${d.count !== 1 ? 's' : ''} at this order`;
                    },
                },
            },
        },
        scales: {
            x: {
                title: { display: true, text: 'Cut Set Order' },
            },
            y: {
                type: 'linear' as const,
                position: 'left' as const,
                title: { display: true, text: 'Contribution (%)' },
                beginAtZero: true,
                ticks: { font: { size: 11 } },
            },
            y1: {
                type: 'linear' as const,
                position: 'right' as const,
                title: { display: true, text: 'Cumulative (%)' },
                min: 0,
                max: 100,
                grid: { drawOnChartArea: false },
                ticks: {
                    font: { size: 11 },
                    callback: (value: string | number): string => `${value}%`,
                },
            },
        },
    }), [coverageData]);

    const handleExportCSV = useCallback((): void => {
        const header = ['Order', 'Cut Sets', 'Probability', 'Contribution (%)', 'Cumulative (%)'].join(',');
        const csvRows = coverageData.barData.map((d, i) => [
            d.order,
            d.count,
            d.probability.toExponential(2),
            d.pct.toFixed(1),
            coverageData.cumulative[i].toFixed(1),
        ].join(','));
        const csv = [header, ...csvRows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `probability_coverage_by_order_${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [coverageData]);

    if (cutSets.length === 0) return null;

    if (totalProbability === 0) {
        return (
            <Box sx={{ px: 2, py: 2, borderBottom: 1, borderColor: 'divider', textAlign: 'center' }}>
                <Typography variant="body2" color="error">
                    Cumulative coverage requires non-zero base event probabilities.
                </Typography>
            </Box>
        );
    }

    // Find the order at which we reach 95% coverage
    const threshold95 = coverageData.cumulative.findIndex((c) => c >= 95);
    const coverageNote = threshold95 >= 0
        ? `Orders 1–${coverageData.orders[threshold95]} cover ${coverageData.cumulative[threshold95].toFixed(1)}% of total probability.`
        : undefined;

    return (
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle2">Probability Coverage by Cut Set Order</Typography>
                <MuiTooltip title="Export to CSV">
                    <IconButton size="small" onClick={handleExportCSV}>
                        <FileDownloadIcon fontSize="small" />
                    </IconButton>
                </MuiTooltip>
            </Stack>
            <Box sx={{ height: 280 }}>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                <Chart ref={chartRef as any} type="bar" data={chartData} options={options} />
            </Box>
            {coverageNote && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {coverageNote} Higher orders may be safely deprioritized.
                </Typography>
            )}
        </Box>
    );
};

export default CumulativeCoverageChart;
